import { locale } from '../../../imports/i18n'
import { safeGet } from '../../../utils/safeGet'
import { cloneObject } from '../../../imports/lib'
import { get as _get, post as _post } from '../../../utils/fetcher'
import { mapScoringProfileDefinition, formatDriverWeightNumber } from '../../../utils/drivers'
import { INIT_DEFINITION_SETTINGS } from '../../../settings/scoringProfilesSettings'

export default {
  namespaced: true,

  state: {
    loading: false,
    isDuplicate: false,
    scoringProfileModelId: null,
    scoringProfileModelName: '',
    scoringProfileAssociated: [],
    scoringProfileModelDrivers: [],
    scoringProfileSelectedFamilies: [],
    scoringProfileModelFamiliesWeights: [],
    defaultScoringProfileModels: {
      scoringProfileModelName: '',
      scoringProfileModelDrivers: {},
    },
  },

  mutations: {
    setLoading(state, data) {
      state.loading = data
    },

    setIsDuplicate(state, payload) {
      state.isDuplicate = payload
    },

    setScoringProfileModelId(state, data) {
      state.scoringProfileModelId = data
    },

    setScoringProfileModelName(state, data) {
      state.scoringProfileModelName = data
    },

    setScoringProfileAssociated(state, data) {
      state.scoringProfileAssociated = data
    },

    setScoringProfileModelDrivers(state, data) {
      state.scoringProfileModelDrivers = data
    },

    setDefaultScoringProfileModel(state, data) {
      state.defaultScoringProfileModels = data
    },

    setScoringProfileSelectedFamilies(state, data) {
      state.scoringProfileSelectedFamilies = data
    },

    setScoringProfileModelFamiliesWeights(state, data) {
      state.scoringProfileModelFamiliesWeights = data
    },
  },

  actions: {
    setIsDuplicate({ commit }, value) {
      commit('setIsDuplicate', value)
    },

    setScoringProfileModelId({ commit }, data) {
      commit('setScoringProfileModelId', data)
    },

    setScoringProfileModelDrivers({ commit }, data) {
      commit('setScoringProfileModelDrivers', data)
    },

    setScoringProfileAssociated({ commit }, data) {
      commit('setScoringProfileAssociated', data)
    },

    setScoringProfileSelectedFamilies({ commit }, data) {
      commit('setScoringProfileSelectedFamilies', data)
    },

    setScoringProfileModelFamiliesWeights({ commit }, data) {
      commit('setScoringProfileModelFamiliesWeights', data)
    },

    setDefaultScoringProfileModel({ commit, dispatch }) {
      commit('setIsDuplicate', false)
      commit('setScoringProfileModelId', null)
      commit('setScoringProfileModelName', '')
      commit('setScoringProfileModelDrivers', [])
      commit('setScoringProfileAssociated', [])
      commit('setScoringProfileModelFamiliesWeights', [])
      commit('setScoringProfileSelectedFamilies', [])
      dispatch('scoringProfilesDefinition/setSelectedWeightType', 1, { root: true })
      dispatch('scoringProfilesDefinition/setSequenceWeightsDecay', 0.5, { root: true })
      dispatch('scoringProfilesDefinition/setScoringDefinitionSettings', INIT_DEFINITION_SETTINGS(), {
        root: true,
      })
    },

    saveScoringProfileModel({ commit, getters, rootGetters }) {
      commit('setLoading', true)
      let drivers = []
      let modelDrivers = getters.getScoringProfileModelDrivers
      Object.keys(modelDrivers).forEach((key) => {
        let list = modelDrivers[key].filter((item) => item.selected && item.weight !== null)
        if (list.length > 0) {
          list = list.map((d) => {
            return {
              family: d.family,
              indicator: d.indicator,
              weight: formatDriverWeightNumber(d.weight),
            }
          })
          drivers.push(...list)
        }
      })

      const model = {
        id: getters.getScoringProfileModelId,
        name: getters.getScoringProfileModelName,
        description: '',
        createdBy: this.authUsername,
        createdAt: new Date().toJSON(),
        lastEditedBy: this.authUsername,
        lastEditedAt: new Date().toJSON(),
        drivers: drivers,
        scoringDefinition: rootGetters['scoringProfilesDefinition/getScoringDefinitionToSendToAPI'],
      }

      return new Promise((resolve, reject) => {
        _post('/api/scoringProfileModels/save', model)
          .then((response) => {
            resolve(response)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            commit('setLoading', false)
          })
      })
    },

    loadScoringProfileModel({ commit, dispatch }, params) {
      commit('setLoading', true)
      return new Promise((resolve, reject) => {
        _get(`/api/scoringProfileModels/${params.id}`)
          .then((resp) => {
            let id = params.id
            let name = resp.name
            if (params.action === 'duplicate') {
              id = null
              name = resp.name + ' - ' + (locale() === 'it' ? 'Copia' : 'Copy')
              commit('setIsDuplicate', true)
            }
            commit('setScoringProfileModelId', id)
            commit('setScoringProfileModelName', name)

            dispatch('scoringProfilesDefinition/setSelectedWeightType', resp.scoringDefinition.weightType, {
              root: true,
            })
            dispatch('scoringProfilesDefinition/setDefaultSelectedWeightType', resp.scoringDefinition.weightType, {
              root: true,
            })

            dispatch(
              'scoringProfilesDefinition/setSequenceWeightsDecay',
              resp.scoringDefinition.geometricDrivers.sequenceWeightsDecay,
              { root: true }
            )
            dispatch(
              'scoringProfilesDefinition/setDefaultSequenceWeightsDecay',
              resp.scoringDefinition.geometricDrivers.sequenceWeightsDecay,
              { root: true }
            )

            const scoringDefinitionSettings = mapScoringProfileDefinition(
              INIT_DEFINITION_SETTINGS(),
              resp.scoringDefinition
            )

            dispatch('scoringProfilesDefinition/setScoringDefinitionSettings', scoringDefinitionSettings, {
              root: true,
            })
            dispatch(
              'scoringProfilesDefinition/setDefaultScoringDefinitionSettings',
              cloneObject(scoringDefinitionSettings),
              { root: true }
            )

            resolve(resp)
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            commit('setLoading', false)
          })
      })
    },

    loadModelForLinking({}, id) {
      return new Promise((resolve, reject) => {
        _get(`/api/scoringProfileModels/${id}`)
          .then((resp) => {
            resolve(resp)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    loadDriversFamiliesIndicators({ commit, dispatch, getters, rootGetters }, params) {
      dispatch('scoringProfilesDefinition/loadDriversIndicators', null, { root: true })
        .then((resp) => {
          let data = Object.assign({}, resp.indicatorsFamilies)
          let selectedFamilies = []
          const keys = Object.keys(data)
          keys.forEach((key) => {
            data[key] = data[key].map((j) => {
              return {
                family: key,
                indicator: j,
                weight: null,
                selected: false,
              }
            })
          })

          if (params && Array.isArray(params.drivers) && params.drivers.length > 0) {
            params.drivers.forEach((d) => {
              const key = Object.keys(data).find((key) => key === d.family)
              data[key].forEach((item) => {
                if (item.indicator === d.indicator) {
                  let weight = (d.weight * 100).toFixed(2)
                  d.weight = parseFloat(weight) + '%'
                  d.selected = true
                  Object.assign(item, d)
                }
              })
              if (data[key].some((item) => item.selected)) {
                selectedFamilies.push(key)
              }
            })
          }
          selectedFamilies = [...new Set(selectedFamilies)]

          commit('setScoringProfileSelectedFamilies', selectedFamilies)
          commit('setScoringProfileModelDrivers', data)
          const defaultScoringProfileModels = {
            scoringProfileModelName: getters.getScoringProfileModelName,
            scoringProfileModelDrivers: cloneObject(data),
          }
          commit('setDefaultScoringProfileModel', defaultScoringProfileModels)
        })
        .catch((err) => {
          return err
        })
    },
  },

  getters: {
    getLoading: (state) => {
      return safeGet(() => state.loading, false)
    },

    getIsDuplicate: (state) => {
      return safeGet(() => state.isDuplicate, false)
    },

    getScoringProfileModelId: (state) => {
      return safeGet(() => state.scoringProfileModelId, null)
    },

    getScoringProfileModelName: (state) => {
      return safeGet(() => state.scoringProfileModelName, '')
    },

    getScoringProfileModelDrivers: (state) => {
      return safeGet(() => state.scoringProfileModelDrivers, [])
    },

    getScoringProfileAssociated: (state) => {
      return safeGet(() => state.scoringProfileAssociated, [])
    },

    getDefaultScoringProfileModel: (state) => {
      return safeGet(() => state.defaultScoringProfileModels, [])
    },

    getScoringProfileSelectedFamilies: (state) => {
      return safeGet(() => state.scoringProfileSelectedFamilies, [])
    },

    getValidDriversFamiliesWeights: (state) => {
      return safeGet(() => state.scoringProfileModelFamiliesWeights, [])
    },

    getScoringProfileModelNameValidation: (state, getters) => {
      const valueName = getters.getScoringProfileModelName
      const isValidName = typeof valueName === 'string' && valueName.trim() != ''
      return safeGet(() => isValidName, false)
    },

    getModelFamiliesIndicatorsWeightsValidation: (state, getters) => {
      let validation = {
        indicators: [],
        validate: false,
      }
      const driversFamiliesWeights = getters.getValidDriversFamiliesWeights
      driversFamiliesWeights.forEach((family) => {
        const items = family.familyIndicators.filter((k) => parseFloat(k.weight) === 0 || isNaN(parseFloat(k.weight)))
        validation.indicators.push(...items)
      })
      validation.validate = validation.indicators.length === 0
      return safeGet(() => validation, validation)
    },

    getScoringProfileModelDiff: (state, getters, rootState, rootGetters) => {
      const modelName = getters.getScoringProfileModelName
      const defaultScoringProfileModel = getters.getDefaultScoringProfileModel
      const scoringProfileModelDrivers = getters.getScoringProfileModelDrivers

      const result = {
        isNameChanged: defaultScoringProfileModel.scoringProfileModelName !== modelName,
        isScoringProfileModelDriversChanged:
          JSON.stringify(defaultScoringProfileModel.scoringProfileModelDrivers) !==
          JSON.stringify(scoringProfileModelDrivers),
        isScoringDefinitionSettingsChanged: rootGetters['scoringProfilesDefinition/getScoringDefinitionDiff'],
      }

      return Object.values(result).some((j) => j === true)
    },
  },
}
